import { useEffect, useState } from 'react';
import type { DayLogsDTO, SaveDayLogsRequest } from '@six-pockets/shared';
import { cashBookApi } from '@/api';
import { DatePicker, MoneyInput } from '@/components/ui';
import { ManageFormModal } from '@/features/manage/components/ManageFormModal';
import { parseWhole } from '@/features/manage/draft';
import { formatDayLabel } from '@/utils/date';
import { notesDraftOf, notesFilled, parseNotes } from '../cashNotes';
import { CashNotesField } from './CashNotesField';

interface CounterLogFormModalProps {
  /** The day it opens on and what is already logged for it; the fields start from it. */
  logs: DayLogsDTO;
  /** The server's day (YYYY-MM-DD): the latest day that can be picked. */
  today: string;
  busy: boolean;
  error: string | null;
  onSave: (day: string, body: SaveDayLogsRequest) => void;
  onClose: () => void;
}

const amountOf = (log: { amount: number } | null) => (log ? String(log.amount) : '');

/**
 * Log a day's cash count (as notes of each value; the count is their total) and UPI count (what the UPI app says it
 * received), or correct them. Either can be left empty and logged later. The day can be changed (staff back from
 * leave log the day they missed); the fields then show what that day already has. Mount it only while open so the
 * fields start fresh.
 */
export function CounterLogFormModal({ logs: opened, today, busy, error, onSave, onClose }: CounterLogFormModalProps) {
  const [logs, setLogs] = useState(opened);
  const [day, setDay] = useState(opened.day);
  const [notes, setNotes] = useState(notesDraftOf(opened.cash?.notes));
  const [cash, setCash] = useState(amountOf(opened.cash));
  const [upi, setUpi] = useState(amountOf(opened.upi));
  const [loadError, setLoadError] = useState<string | null>(null);

  // Another day picked: load what it already has.
  const loading = logs.day !== day && !loadError;
  useEffect(() => {
    if (logs.day === day) return;
    const ctrl = new AbortController();
    cashBookApi.day(day, ctrl.signal).then(
      (l) => {
        setLogs(l);
        setNotes(notesDraftOf(l.cash?.notes));
        setCash(amountOf(l.cash));
        setUpi(amountOf(l.upi));
      },
      (err) => {
        if (!ctrl.signal.aborted) setLoadError(err instanceof Error ? err.message : 'Could not load that day');
      },
    );
    return () => ctrl.abort();
  }, [day, logs.day]);

  const pickDay = (d: string) => {
    setLoadError(null);
    setDay(d);
  };

  const isToday = day === today;
  const logged = logs.day === day && (logs.cash || logs.upi);
  // A cash count from before notes were counted is corrected as one amount, as it was logged.
  const asAmount = !loading && !!logs.cash && !logs.cash.notes;
  const cashFilled = asAmount ? cash.trim() !== '' : notesFilled(notes);
  const upiFilled = upi.trim() !== '';
  const blocked =
    loading ? 'Loading that day…'
    : loadError ? 'That day could not be loaded'
    : !cashFilled && !upiFilled ? `Enter the ${asAmount ? 'cash count' : 'notes counted'} or the UPI count`
    : cashFilled && !asAmount && parseNotes(notes) === null ? 'Use whole numbers for the notes and coins'
    : (cashFilled && asAmount && parseWhole(cash) === null) || (upiFilled && parseWhole(upi) === null) ? 'Use whole rupees'
    : undefined;

  const cashHint = !isToday && !loading && !logs.cash ? "Recorded as that day's closing count" : undefined;

  const title = isToday ? (logged ? "Edit today's count" : "Today's count") : `${logged ? 'Edit count' : 'Add count'} · ${formatDayLabel(day)}`;

  return (
    <ManageFormModal
      title={title}
      submitLabel={logged ? 'Save changes' : 'Save count'}
      busy={busy}
      error={loadError ?? error}
      blockedReason={blocked}
      onSubmit={() =>
        onSave(day, {
          ...(asAmount ?
            { cash: parseWhole(cash) ?? undefined }
          : { cashNotes: cashFilled ? (parseNotes(notes) ?? undefined) : undefined }),
          upi: parseWhole(upi) ?? undefined,
        })
      }
      onClose={onClose}
    >
      <DatePicker value={day} onChange={pickDay} max={today} label="Day counted" fieldLabel="Day" inline />
      {asAmount ?
        <MoneyInput label="Cash in the counter" hint="Logged before notes were counted" value={cash} onChange={setCash} data-autofocus />
      : <CashNotesField value={notes} onChange={setNotes} disabled={loading} hint={cashHint} />}
      <MoneyInput
        label="UPI received"
        hint={`As the UPI app shows for ${isToday ? 'today' : 'that day'}`}
        value={upi}
        onChange={setUpi}
        disabled={loading}
      />
    </ManageFormModal>
  );
}
